import { useEffect, useState } from "react";
import type { Dashboard as Data } from "../cloud/api";
import { itemById } from "../content/items";
import { OPS, type Op } from "../content/modes";
import { questionType } from "../game/questions/types";
import "./Dashboard.css";

interface Props {
  childId: string;
  name: string;
  /** When this changes (after a sync), the figures are fetched again. */
  version: string | null;
}

const OP_LABEL: Record<Op, string> = {
  add: "Adding",
  sub: "Taking away",
  mul: "Times",
  div: "Sharing",
};
/** Categorical slots 1-4, in fixed order (validated light and dark; see CSS). */
const OP_VAR: Record<Op, string> = {
  add: "var(--series-1)",
  sub: "var(--series-2)",
  mul: "var(--series-3)",
  div: "var(--series-4)",
};

const pct = (right: number, tried: number) => (tried ? Math.round((right / tried) * 100) : 0);
const secs = (ms: number | null) => (ms === null ? "–" : `${(ms / 1000).toFixed(1)}s`);
const shortDate = (d: string) =>
  new Date(d + "T12:00").toLocaleDateString(undefined, { day: "numeric", month: "short" });

/** Accuracy per strand per week: one line per strand, 0-100%. */
function WeeklyChart({ weekly }: { weekly: Data["weekly"] }) {
  const [hover, setHover] = useState<number | null>(null);
  const weeks = [...new Set(weekly.map((w) => w.week))].sort();
  const ops = OPS.filter((op) => weekly.some((w) => w.op === op));
  if (weeks.length < 2 || !ops.length)
    return <p className="hint">The weekly chart appears after two weeks of play.</p>;

  const W = 900;
  const H = 280;
  const pad = { l: 40, r: 96, t: 12, b: 26 };
  const x = (i: number) => pad.l + (i / (weeks.length - 1)) * (W - pad.l - pad.r);
  const y = (p: number) => pad.t + (1 - p / 100) * (H - pad.t - pad.b);
  const at = (op: Op, week: string) => weekly.find((w) => w.op === op && w.week === week);

  return (
    <figure className="viz">
      <div className="legend" aria-hidden="true">
        {ops.map((op) => (
          <span key={op}>
            <i style={{ background: OP_VAR[op] }} />
            {OP_LABEL[op]}
          </span>
        ))}
      </div>
      <div className="viz-wrap">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Accuracy by week for each type of maths"
          onPointerLeave={() => setHover(null)}
        >
          {[0, 50, 100].map((p) => (
            <g key={p}>
              <line className="grid" x1={pad.l} x2={W - pad.r} y1={y(p)} y2={y(p)} />
              <text className="tick" x={pad.l - 6} y={y(p) + 4} textAnchor="end">
                {p}%
              </text>
            </g>
          ))}
          {weeks.map((w, i) =>
            i === 0 || i === weeks.length - 1 ? (
              <text key={w} className="tick" x={x(i)} y={H - 6} textAnchor="middle">
                {shortDate(w)}
              </text>
            ) : null,
          )}
          {hover !== null && (
            <line className="crosshair" x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} />
          )}
          {ops.map((op) => {
            const pts = weeks
              .map((w, i) => {
                const r = at(op, w);
                return r ? { i, p: pct(r.right, r.tried) } : null;
              })
              .filter((v): v is { i: number; p: number } => v !== null);
            const last = pts[pts.length - 1];
            return (
              <g key={op} style={{ color: OP_VAR[op] }}>
                <polyline
                  className="line"
                  points={pts.map((v) => `${x(v.i)},${y(v.p)}`).join(" ")}
                />
                {pts.map((v) => (
                  <circle key={v.i} className="dot" cx={x(v.i)} cy={y(v.p)} r={4} />
                ))}
                {last && (
                  <text className="end-label" x={x(last.i) + 8} y={y(last.p) + 4}>
                    {OP_LABEL[op]}
                  </text>
                )}
              </g>
            );
          })}
          {weeks.map((w, i) => (
            <rect
              key={w}
              className="hit"
              x={x(i) - (W - pad.l - pad.r) / (2 * (weeks.length - 1))}
              y={0}
              width={(W - pad.l - pad.r) / (weeks.length - 1)}
              height={H}
              onPointerEnter={() => setHover(i)}
              onPointerDown={() => setHover(i)}
            />
          ))}
        </svg>
        {hover !== null && (
          <div className="tip" style={{ left: `${(x(hover) / W) * 100}%` }} role="status">
            <b>Week of {shortDate(weeks[hover]!)}</b>
            {ops.map((op) => {
              const r = at(op, weeks[hover]!);
              return r ? (
                <span key={op}>
                  <i style={{ background: OP_VAR[op] }} />
                  {OP_LABEL[op]}: {pct(r.right, r.tried)}% of {r.tried}
                </span>
              ) : null;
            })}
          </div>
        )}
      </div>
      <details className="viz-table">
        <summary>Show the numbers</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Week of</th>
              {ops.map((op) => (
                <th key={op} scope="col">
                  {OP_LABEL[op]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w}>
                <td>{shortDate(w)}</td>
                {ops.map((op) => {
                  const r = at(op, w);
                  return <td key={op}>{r ? `${pct(r.right, r.tried)}%` : "–"}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

export function minutesText(m: number): string {
  if (m <= 0) return "0 minutes";
  if (m < 1) return "under a minute";
  const n = Math.round(m);
  return `${n} minute${n === 1 ? "" : "s"}`;
}

/** Minutes played per day, trials and practice stacked. */
function DailyChart({ daily }: { daily: Data["daily"] }) {
  const [hover, setHover] = useState<number | null>(null);
  const days: string[] = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const p = (n: number) => String(n).padStart(2, "0");
    days.push(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`);
  }
  const rows = days.map((d) => {
    const r = daily.find((x) => x.day === d);
    return {
      d,
      trial: (r?.trial_ms ?? 0) / 60000,
      practice: (r?.practice_ms ?? 0) / 60000,
      battle: (r?.battle_ms ?? 0) / 60000,
    };
  });
  const max = Math.max(5, ...rows.map((r) => r.trial + r.practice + r.battle));
  const total = rows.reduce((n, r) => n + r.trial + r.practice + r.battle, 0);

  const W = 900;
  const H = 200;
  const pad = { l: 40, r: 8, t: 12, b: 26 };
  const bw = (W - pad.l - pad.r) / rows.length;
  const y = (m: number) => pad.t + (1 - m / max) * (H - pad.t - pad.b);
  /** Bar height; anything played shows at least a sliver. */
  const h = (m: number) => (m > 0 ? Math.max(3, (m / max) * (H - pad.t - pad.b)) : 0);
  const base = H - pad.b;

  return (
    <figure className="viz">
      <div className="legend" aria-hidden="true">
        <span>
          <i style={{ background: "var(--series-1)" }} />
          Trials
        </span>
        <span>
          <i style={{ background: "var(--series-2)" }} />
          Practice
        </span>
        <span>
          <i style={{ background: "var(--series-3)" }} />
          Battles
        </span>
        <span className="legend-total">{minutesText(total)} in 14 days</span>
      </div>
      <div className="viz-wrap">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={`Time played per day: ${minutesText(total)} in the last 14 days`}
          onPointerLeave={() => setHover(null)}
        >
          {[0, Math.round(max / 2), Math.round(max)].map((m) => (
            <g key={m}>
              <line className="grid" x1={pad.l} x2={W - pad.r} y1={y(m)} y2={y(m)} />
              <text className="tick" x={pad.l - 6} y={y(m) + 4} textAnchor="end">
                {m}m
              </text>
            </g>
          ))}
          {rows.map((r, i) => {
            const bx = pad.l + i * bw + 3;
            const w = bw - 6;
            // Practice sits on trials, battles on practice, with 2px gaps; all grow up from the baseline.
            const th = h(r.trial);
            const ph = h(r.practice);
            const bh = h(r.battle);
            const gap = th && ph ? 2 : 0;
            const gap2 = bh && (th || ph) ? 2 : 0;
            return (
              <g key={r.d}>
                {th > 0 && (
                  <rect
                    className="viz-bar"
                    style={{ fill: "var(--series-1)" }}
                    x={bx}
                    y={base - th}
                    width={w}
                    height={th}
                    rx={Math.min(4, th / 2)}
                    ry={Math.min(4, th / 2)}
                  />
                )}
                {ph > 0 && (
                  <rect
                    className="viz-bar"
                    style={{ fill: "var(--series-2)" }}
                    x={bx}
                    y={base - th - gap - ph}
                    width={w}
                    height={ph}
                    rx={Math.min(4, ph / 2)}
                    ry={Math.min(4, ph / 2)}
                  />
                )}
                {bh > 0 && (
                  <rect
                    className="viz-bar"
                    style={{ fill: "var(--series-3)" }}
                    x={bx}
                    y={base - th - gap - ph - gap2 - bh}
                    width={w}
                    height={bh}
                    rx={Math.min(4, bh / 2)}
                    ry={Math.min(4, bh / 2)}
                  />
                )}
                <rect
                  className="hit"
                  x={pad.l + i * bw}
                  y={0}
                  width={bw}
                  height={H}
                  onPointerEnter={() => setHover(i)}
                  onPointerDown={() => setHover(i)}
                />
              </g>
            );
          })}
          <text className="tick" x={pad.l + bw / 2} y={H - 6} textAnchor="middle">
            {shortDate(days[0]!)}
          </text>
          <text className="tick" x={W - pad.r - bw / 2} y={H - 6} textAnchor="middle">
            Today
          </text>
        </svg>
        {hover !== null && (
          <div
            className="tip"
            style={{ left: `${((pad.l + (hover + 0.5) * bw) / W) * 100}%` }}
            role="status"
          >
            <b>{shortDate(rows[hover]!.d)}</b>
            <span>
              <i style={{ background: "var(--series-1)" }} />
              Trials: {minutesText(rows[hover]!.trial)}
            </span>
            <span>
              <i style={{ background: "var(--series-2)" }} />
              Practice: {minutesText(rows[hover]!.practice)}
            </span>
            <span>
              <i style={{ background: "var(--series-3)" }} />
              Battles: {minutesText(rows[hover]!.battle)}
            </span>
          </div>
        )}
      </div>
    </figure>
  );
}

/** The parent dashboard for one child, from the family's server data. */
export function Dashboard({ childId, name, version }: Props) {
  const [data, setData] = useState<Data | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    import("../cloud/api")
      .then(({ childDashboard }) => childDashboard(childId))
      .then((d) => {
        if (live) setData(d);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [childId, version]);

  if (failed) return <p className="hint">Couldn&apos;t load the dashboard. Check the internet.</p>;
  if (!data) return <p className="hint">Loading {name}&apos;s dashboard…</p>;

  const skills = data.skills
    .map((s) => ({ ...s, type: questionType(s.type_id), acc: pct(s.right, s.tried) }))
    .filter((s) => s.type)
    .sort((a, b) => a.acc - b.acc || b.tried - a.tried);

  return (
    <div className="dashboard viz-root">
      <h3 className="dash-h">Accuracy and speed by skill</h3>
      {skills.length ? (
        <table className="skills">
          <thead>
            <tr>
              <th scope="col">Skill</th>
              <th scope="col">Tried</th>
              <th scope="col">Right</th>
              <th scope="col">Typical time</th>
            </tr>
          </thead>
          <tbody>
            {skills.map((s) => {
              const shaky = s.tried >= 10 && s.acc < 80;
              const slow = s.median_ms !== null && s.median_ms > 6000;
              return (
                <tr key={s.type_id}>
                  <td>
                    {OP_LABEL[s.type!.op]}: {s.type!.label}
                    {shaky && slow && <span className="flag"> · needs practice</span>}
                  </td>
                  <td>{s.tried}</td>
                  <td>{s.acc}%</td>
                  <td>{secs(s.median_ms)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="hint">Shows up after a trial or some practice.</p>
      )}
      <p className="hint">
        Typical time is the middle time for right answers. &ldquo;Needs practice&rdquo; marks skills
        that are both under 80% and over 6 seconds.
      </p>
      <h3 className="dash-h">Accuracy by week</h3>
      <WeeklyChart weekly={data.weekly} />
      <h3 className="dash-h">Time played</h3>
      <DailyChart daily={data.daily} />
      <h3 className="dash-h">Battles</h3>
      <p className="hint">
        {data.battles && data.battles.fought > 0
          ? `${data.battles.fought} fought, ${data.battles.won} won, ${data.battles.recruited} ${data.battles.recruited === 1 ? "ninja" : "ninjas"} beaten.`
          : "No battles yet."}
      </p>
      <h3 className="dash-h">Coins and items</h3>
      <p className="hint">
        {data.coins
          ? `${data.coins.earned} coins earned, ${data.coins.spent} spent in the Dojo Shop.`
          : "No coins yet."}
        {data.items?.length
          ? ` Owns ${data.items
              .map(
                (i) =>
                  `${itemById(i.item)?.name ?? i.item}${i.level > 1 ? ` (level ${i.level})` : ""}`,
              )
              .join(", ")}.`
          : " Nothing bought yet."}
      </p>
    </div>
  );
}
